/**
 * Sistema de toasts global (sin lib externa).
 *
 * Uso:
 *   const { showToast } = useToast();
 *   showToast({ kind: "success", title: "Project creado", message: "Clonando repo..." });
 *
 * Cada toast se auto-cierra a los 5s (configurable). Stack vertical
 * en top-right (estandar).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ToastKind = "success" | "info" | "warn" | "error";

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
  durationMs?: number;
}

interface ToastContextValue {
  showToast: (t: Omit<Toast, "id">) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeouts = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const h = timeouts.current.get(id);
    if (h) {
      clearTimeout(h);
      timeouts.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (t: Omit<Toast, "id">): number => {
      const id = nextId++;
      const next: Toast = { id, durationMs: 5000, ...t };
      setToasts((prev) => [...prev, next]);
      if (next.durationMs && next.durationMs > 0) {
        const h = setTimeout(() => dismiss(id), next.durationMs);
        timeouts.current.set(id, h);
      }
      return id;
    },
    [dismiss],
  );

  // Cleanup en unmount
  useEffect(() => {
    const handles = timeouts.current;
    return () => {
      handles.forEach((h) => clearTimeout(h));
      handles.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast debe usarse dentro de <ToastProvider>");
  return ctx;
}

const KIND_CLASS: Record<ToastKind, string> = {
  success: "border-green-200 bg-green-50 text-green-900",
  info: "border-blue-200 bg-blue-50 text-blue-900",
  warn: "border-yellow-300 bg-yellow-50 text-yellow-900",
  error: "border-red-200 bg-red-50 text-red-900",
};

const KIND_ICON: Record<ToastKind, string> = {
  success: "✓",
  info: "ℹ",
  warn: "⚠",
  error: "✕",
};

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-80 flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`pointer-events-auto rounded-lg border px-3 py-2.5 shadow-lg ${KIND_CLASS[t.kind]}`}
        >
          <div className="flex items-start gap-2">
            <span className="text-sm font-bold">{KIND_ICON[t.kind]}</span>
            <div className="flex-1">
              <div className="text-sm font-semibold">{t.title}</div>
              {t.message && <div className="mt-0.5 text-xs">{t.message}</div>}
            </div>
            <button
              type="button"
              aria-label="Cerrar"
              onClick={() => onDismiss(t.id)}
              className="text-current/60 hover:text-current"
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
